/**
 * AgentStepCards — Manus-style step-by-step view
 *
 * Each planning step is rendered as a collapsible card:
 * - Simple check icon when done
 * - Animated spinner when active
 * - Small dot when pending
 * - Warning triangle for errors (amber, not red)
 * - Action chips (tool calls) shown under each step
 * - Prose text (agent thinking) shown as a subtle paragraph
 * - Copy button on each step (#18)
 * - Colored log levels (#19)
 *
 * The entire panel starts COLLAPSED by default — user clicks chevron to expand.
 */
import { useState, useCallback } from 'react'
import { Check, X, Globe, ChevronDown, ChevronRight, Copy, Monitor, Edit3, Plug } from 'lucide-react'
import type { PlanningTask, ActionChip, AgentLogEntry, AgentState } from '@/hooks/useAgent'
import { SineThinkingLoader } from '@/components/ui/SineThinkingLoader'
import { buildComputerPanelModel, cleanComputerSnippet, isNoisyComputerLine } from '@/lib/computerPanelModel'

// ── Tool icon mapping ─────────────────────────────────────────────────────────
const TOOL_ICONS: Record<string, string> = {
  terminal:   '⌨',
  write_file: '✏',
  read_file:  '📄',
  list_files: '📁',
  web_search: '__globe__',  // rendered as <Globe> component
  git_clone:  '⎇',
  browser:    '🌐',
  run_code:   '▶',
}

const TOOL_COLORS: Record<string, string> = {
  terminal:   'chip-terminal',
  write_file: 'chip-write',
  read_file:  'chip-read',
  list_files: 'chip-list',
  web_search: 'chip-search',
  git_clone:  'chip-git',
  browser:    'chip-browser',
  run_code:   'chip-run',
}

// ── #18: Copy button ──────────────────────────────────────────────────────────
function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    }).catch(() => {})
  }, [text])
  return (
    <button
      className="agent-step-copy-btn"
      onClick={e => { e.stopPropagation(); handleCopy(); }}
      title="Kopier steg-tekst"
      type="button"
    >
      {copied ? <Check size={10} /> : <Copy size={10} />}
    </button>
  )
}

// ── Single chip ───────────────────────────────────────────────────────────────
function ActionChipBadge({ chip }: { chip: ActionChip }) {
  const icon = TOOL_ICONS[chip.tool] ?? '⚙'
  const colorClass = TOOL_COLORS[chip.tool] ?? 'chip-default'
  const stateClass =
    chip.success === true  ? 'chip-success' :
    chip.success === false ? 'chip-warning'   :
    'chip-running'

  // Truncate long labels
  const shortLabel = chip.label.length > 45 ? chip.label.slice(0, 42) + '…' : chip.label

  return (
    <span className={`agent-chip ${colorClass} ${stateClass}`} title={chip.label}>
      <span className="agent-chip-icon">
        {icon === '__globe__' ? <Globe size={11} /> : icon}
      </span>
      <span className="agent-chip-label">{shortLabel}</span>
      {chip.success === undefined && (
        <span className="agent-chip-spinner" />
      )}
      {chip.success === true && (
        <span className="agent-chip-check">✓</span>
      )}
      {chip.success === false && (
        <span className="agent-chip-warn">!</span>
      )}
    </span>
  )
}

// ── Step icon ─────────────────────────────────────────────────────────────────
function StepIcon({ status }: { status: PlanningTask['status'] }) {
  if (status === 'done') {
    return (
      <span className="step-icon step-icon--done">
        <Check size={12} strokeWidth={3} />
      </span>
    )
  }
  if (status === 'active') {
    return <SineThinkingLoader size="xs" showText={false} label="Aktiv" className="step-icon step-icon--active" />
  }
  if (status === 'error') {
    return (
      <span className="step-icon step-icon--error-neutral">
        <X size={11} strokeWidth={2.5} />
      </span>
    )
  }
  // Pending: small dot
  return <span className="step-icon step-icon--pending-dot" />
}

// ── #19: Colored prose by log level ──────────────────────────────────────────
function ProseBlock({ text }: { text: string }) {
  const lower = text.toLowerCase()
  const isError   = text.startsWith('✗') || lower.startsWith('error') || lower.startsWith('feil')
  const isSuccess = text.startsWith('✓') || lower.startsWith('ferdig') || lower.startsWith('done')
  const isWarning = lower.startsWith('advarsel') || lower.startsWith('warning')
  const cls = isError ? 'prose-warning' : isSuccess ? 'prose-success' : isWarning ? 'prose-warning' : 'prose-default'
  return <p className={`agent-step-card__prose ${cls}`}>{text}</p>
}

// ── Single step card ──────────────────────────────────────────────────────────
function StepCard({ step, defaultOpen }: { step: PlanningTask; defaultOpen: boolean }) {
  const [open, setOpen] = useState(defaultOpen)
  const [hovered, setHovered] = useState(false)
  const hasChips = (step.chips?.length ?? 0) > 0
  const hasProse = !!step.prose
  const hasContent = hasChips || hasProse
  const isActive = step.status === 'active'

  return (
    <div
      className={`agent-step-card agent-step-card--${step.status}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Header row */}
      <button
        className="agent-step-card__header"
        onClick={() => hasContent && setOpen(o => !o)}
        type="button"
        aria-expanded={open}
        disabled={!hasContent}
      >
        <StepIcon status={step.status} />
        <span className={`agent-step-card__label${isActive ? ' agent-step-card__label--active' : ''}`}>
          {step.label}
        </span>
        {/* #18: Copy button appears on hover */}
        {hovered && (
          <CopyButton text={step.label} />
        )}
        {hasContent && (
          open
            ? <ChevronDown size={12} className="agent-step-card__chevron" />
            : <ChevronRight size={12} className="agent-step-card__chevron" />
        )}
      </button>

      {/* Expandable body */}
      {open && hasContent && (
        <div className="agent-step-card__body">
          {/* #19: Prose with colored log levels */}
          {hasProse && (
            <ProseBlock text={step.prose!} />
          )}
          {/* Action chips */}
          {hasChips && (
            <div className="agent-step-card__chips">
              {(step.chips ?? []).map(chip => (
                <ActionChipBadge key={chip.id} chip={chip} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Progress bar ──────────────────────────────────────────────────────────────
function ProgressBar({ done, total }: { done: number; total: number }) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0
  return (
    <div className="agent-steps-progress">
      <div className="agent-steps-progress__bar">
        <div className="agent-steps-progress__fill" style={{ width: `${pct}%` }} />
      </div>
      <span className="agent-steps-progress__text">{done}/{total}</span>
    </div>
  )
}

// ── Mini computer preview ────────────────────────────────────────────────────
function MiniComputerPreview({
  agentState,
  logs,
  onOpen,
  isOpen,
}: {
  agentState: AgentState
  logs: AgentLogEntry[]
  onOpen: () => void
  isOpen: boolean
}) {
  const model = buildComputerPanelModel(agentState)
  const lastLog = logs[logs.length - 1]
  const lastSemanticEvent = [...(agentState.semanticEvents ?? [])]
    .reverse()
    .find(event => !isNoisyComputerLine(`${event.title ?? ''} ${event.text}`))
  const browserScreenshotSrc = agentState.browserFrame?.screenshot
    ? (agentState.browserFrame.screenshot.startsWith('data:')
      ? agentState.browserFrame.screenshot
      : `data:image/jpeg;base64,${agentState.browserFrame.screenshot}`)
    : null
  const semanticTitle = cleanComputerSnippet(
    lastSemanticEvent
      ? [lastSemanticEvent.title, lastSemanticEvent.text].filter(Boolean).join(': ')
      : model.target,
    model.target,
  )
  const title = model.mode === 'browser'
    ? cleanComputerSnippet(agentState.browserFrame?.title || agentState.browserFrame?.url || semanticTitle, 'Browser')
    : model.mode === 'editor'
      ? cleanComputerSnippet(model.activeFile?.split('/').pop() || model.target, 'Editor')
      : model.mode === 'terminal'
        ? cleanComputerSnippet(model.activeCommand || lastLog?.message, 'Terminal')
        : model.mode === 'access'
          ? 'Takeover'
          : model.completionState === 'completed'
            ? 'Fullført'
            : 'Sine'
  const detail = model.mode === 'browser'
    ? cleanComputerSnippet(agentState.browserFrame?.action || agentState.browserFrame?.message || semanticTitle, 'Live browser')
    : model.mode === 'editor'
      ? cleanComputerSnippet(model.activeFile || model.statusLine, 'Leser fil')
      : model.mode === 'terminal'
        ? cleanComputerSnippet(lastLog?.message || model.statusLine, 'Kjører kommando')
        : cleanComputerSnippet(model.statusLine, model.target)

  return (
    <button
      className={`mini-terminal-preview mini-computer-preview mini-computer-preview--${model.mode}${isOpen ? ' active' : ''}`}
      onClick={e => { e.stopPropagation(); onOpen() }}
      title={`Åpne Sine Computer - ${model.statusLine}`}
      type="button"
    >
      <div className="mini-terminal-screen">
        <div className="mini-terminal-scanlines" />
        <div className={`mini-computer-live-dot ${model.completionState}`} />
        {model.mode === 'browser' && browserScreenshotSrc ? (
          <img className="mini-computer-browser-shot" src={browserScreenshotSrc} alt="Live browser" />
        ) : model.mode === 'browser' ? (
          <div className="mini-computer-browser-fallback">
            <Globe size={13} />
            <span>{title}</span>
          </div>
        ) : model.mode === 'editor' ? (
          <div className="mini-computer-editor">
            <div className="mini-computer-editor-bar">
              <Edit3 size={8} />
              <span>{title}</span>
            </div>
            <div className="mini-computer-code-lines">
              <span />
              <span />
              <span />
              <span />
            </div>
          </div>
        ) : model.mode === 'terminal' ? (
          <div className="mini-computer-terminal">
            <span>$</span>
            <p>{detail}</p>
          </div>
        ) : model.mode === 'access' ? (
          <div className="mini-computer-access">
            <Plug size={13} />
            <span>{title}</span>
          </div>
        ) : (
          <div className="mini-computer-summary">
            {model.completionState === 'completed' ? <Check size={14} /> : <Monitor size={14} />}
            <span>{semanticTitle}</span>
          </div>
        )}
      </div>
    </button>
  )
}

// ── Main component ────────────────────────────────────────────────────────────
interface AgentStepCardsProps {
  steps: PlanningTask[]
  isActive?: boolean
  metrics?: { total_tokens?: number; duration_seconds?: number } | null
  logs?: AgentLogEntry[]
  agentState?: AgentState
  onOpenPanel?: () => void
  isPanelOpen?: boolean
}

export function AgentStepCards({ steps, isActive = false, metrics, logs = [], agentState, onOpenPanel, isPanelOpen = false }: AgentStepCardsProps) {
  // Start COLLAPSED — user clicks to expand (Manus-style)
  const [collapsed, setCollapsed] = useState(true)

  if (steps.length === 0) return null

  const doneCount = steps.filter(s => s.status === 'done').length
  const hasError = steps.some(s => s.status === 'error')
  const total = steps.length
  return (
    <div className="agent-step-cards">
      {/* Collapsible header — shows current step summary */}
      <div className="agent-step-cards__toggle-wrap">
        {/* Mini computer preview — absolutely positioned at left, does not affect row height */}
        {onOpenPanel && agentState && (
          <MiniComputerPreview agentState={agentState} logs={logs} onOpen={onOpenPanel} isOpen={isPanelOpen} />
        )}
        <button
          className={`agent-step-cards__toggle${hasError ? ' is-error' : ''}`}
          onClick={() => setCollapsed(c => !c)}
          type="button"
          style={{ paddingLeft: onOpenPanel && agentState ? '116px' : undefined }}
        >
          <span className="agent-step-cards__toggle-left">
            {isActive ? (
              <SineThinkingLoader size="xs" showText={false} label="Aktiv" />
            ) : hasError ? (
              <X size={12} className="agent-step-cards__toggle-error" />
            ) : (
              <Check size={12} className="agent-step-cards__toggle-done" />
            )}
            <span className="agent-step-cards__toggle-label">
              {isActive
                ? 'Tenker'
                : 'Fullført'}
            </span>
          </span>
          <span className="agent-step-cards__toggle-right">
            <ProgressBar done={doneCount} total={total} />
            {collapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
          </span>
        </button>
      </div>

      {/* Step list — hidden by default */}
      {!collapsed && (
        <div className="agent-step-cards__list">
          {steps.map((step, i) => (
            <StepCard
              key={step.id ?? i}
              step={step}
              defaultOpen={step.status === 'active'}
            />
          ))}
        </div>
      )}

      {/* Metrics footer (shown after completion) */}
      {!isActive && metrics && !collapsed && (
        <div className="agent-step-cards__metrics">
          {metrics.total_tokens !== undefined && (
            <span>{metrics.total_tokens.toLocaleString('no')} tokens</span>
          )}
          {metrics.duration_seconds !== undefined && (
            <span>{metrics.duration_seconds.toFixed(0)}s</span>
          )}
        </div>
      )}
    </div>
  )
}
